import { ethers } from 'ethers'
import { amountFromWei } from '../../../../utils/amount'
import { TransactionEvent } from '../../../../types/reducerTypes'
import { useLazyGetMemecoinTransactionsByAddressDateRangeQuery } from '../../../../modules/memecoins/api'
import {
  LibrarySymbolInfo,
  ResolutionString,
  SubscribeBarsCallback,
} from '../../../../charting_library'
import { useMemo } from 'react'

const SUPPORTED_RESOLUTIONS = [
  '1',
  '5',
  '15',
  '30',
  '60',
  '360',
  '720',
  '1D',
] as const

export type SupportedResolutions = (typeof SUPPORTED_RESOLUTIONS)[number]

const config = {
  // optional
  supports_search: false,
  // optional
  symbol_search_request_delay: 0,
  // optional
  supports_group_request: false,
  // optional
  supports_marks: false,
  // optional
  supports_timescale_marks: false,
  // optional
  supports_time: false,
  // symbols_types: ['crypto'],
  supported_resolutions: SUPPORTED_RESOLUTIONS,
  currency_codes: [
    // can also do like { id: "USD", code: "USD", description: "$" }
    'USD',
    'ETH',
  ],
}

type SymbolResolveExtension = {
  currencyCode?: string
  session?: string
  unitId?: string
}

type Bar = {
  close: number
  high: number
  low: number
  open: number
  time: number // milliseconds
  volume?: number // optional
}

type HistoryMetadata = {
  noData?: boolean // optional - value indicating to the library that there is no more data on the server.
  nextTime?: number // optional - The time (in milliseconds) of the next available bar in history if there is noData
}

const useDataFeed = ({
  tokenName,
  tokenSymbol,
  contractAddress,
}: {
  tokenName: string
  tokenSymbol: string
  contractAddress: string
}) => {
  const [fetchTransactions] =
    useLazyGetMemecoinTransactionsByAddressDateRangeQuery()

  return useMemo(
    () => ({
      onReady: (cb: any) => {
        console.log('[onReady]: Method call')
        setTimeout(() => {
          cb(config)
        }, 0)
      },
      searchSymbols: (
        userInput: string,
        exchange: string,
        symbolType: string,
        onResult: (
          items: {
            description: string
            exchange: string
            symbol: string
            ticker: string
            type: string
          }[],
        ) => void,
      ) => {
        console.log('[searchSymbols]: Method call')
        onResult([])
      },
      resolveSymbol: (
        symbolName: string,
        onResolve: (symbolInfo: LibrarySymbolInfo) => void,
        onError: (msg: string) => void,
        extension?: SymbolResolveExtension,
      ) => {
        console.log('[resolveSymbol]: Method call')
        try {
          setTimeout(() => {
            onResolve({
              ticker: tokenSymbol,
              name: tokenName,
              description: tokenSymbol,
              // type: symbolItem.type
              session: '24x7', // this is open/close times of the trading day, in our case, it's always 24x7
              timezone: 'Etc/UTC',
              format: 'price',
              exchange: 'ZAP',
              listed_exchange: 'ZAP',
              type: 'crypto',
              pricescale: 10 ** 12,
              minmov: 1,
              has_intraday: true,
              has_seconds: false,
              // data_status: 'streaming', // 'streaming'
            })
          }, 0)
        } catch (err) {
          onError(err.message)
        }
      },
      getBars: async (
        symbolInfo: LibrarySymbolInfo,
        resolution: ResolutionString,
        periodParams: {
          countBack?: number // optional
          firstDataRequest: boolean
          from: number
          to: number
        },
        onResult: (bars: Bar[], meta?: HistoryMetadata) => void,
        onError: (msg: string) => void,
      ) => {
        console.log('[getBars]: Method call')

        try {
          const { transactions, meta } = await fetchTransactions({
            address: contractAddress,
            from: periodParams.from,
            to: periodParams.to,
          }).unwrap()

          const bars = processTransactions(
            transactions,
            resolution as SupportedResolutions,
          )

          console.log({
            bars,
            noData: bars.length === 0 || bars.length === meta.total,
            count: meta.count,
            total: meta.total,
          })
          onResult(bars, {
            noData: bars.length === 0 || meta.count === meta.total,
          })
        } catch (error) {
          console.error(error)
          onError(error.message)
        }
      },
      subscribeBars: async (
        symbolInfo: LibrarySymbolInfo,
        resolution: ResolutionString,
        onTick: SubscribeBarsCallback,
        listenerGuid: string,
        onResetCacheNeededCallback: Function,
      ) => {
        console.log('[subscribeBars]: Method call')
      },
      unsubscribeBars: async (listenerGuid: string) => {
        console.log('[unsubscribeBars]: Method call')
      },
    }),
    [contractAddress, fetchTransactions, tokenName, tokenSymbol],
  )
}

function getTransactionsInterval(
  timestamp: string,
  timeframe: SupportedResolutions,
) {
  const date = new Date(timestamp)
  const floorDate = new Date(date) // Copy the date object

  switch (timeframe) {
    case '1':
      floorDate.setSeconds(0, 0)
      break
    case '5':
      floorDate.setMinutes(Math.floor(date.getMinutes() / 5) * 5, 0, 0)
      break
    case '15':
      floorDate.setMinutes(Math.floor(date.getMinutes() / 15) * 15, 0, 0)
      break
    case '30':
      floorDate.setMinutes(Math.floor(date.getMinutes() / 30) * 30, 0, 0)
      break
    case '60':
      floorDate.setMinutes(0, 0, 0)
      break
    case '360':
      floorDate.setHours(Math.floor(date.getHours() / 6) * 6, 0, 0, 0)
      break
    case '720':
      floorDate.setHours(Math.floor(date.getHours() / 12) * 12, 0, 0, 0)
      break
    case '1D':
      floorDate.setHours(0, 0, 0, 0)
      break
    default:
      floorDate.setMinutes(0, 0, 0)
      break
  }

  return floorDate.getTime()
}

function processTransactions(
  transactions: TransactionEvent[],
  resolution: SupportedResolutions,
): Bar[] {
  if (!SUPPORTED_RESOLUTIONS.includes(resolution)) {
    throw new Error(
      `Unsupported resolution ${resolution}. Supported resolutions are ${SUPPORTED_RESOLUTIONS.join(
        ', ',
      )}`,
    )
  }
  const groupedData: {
    [key: number]: {
      open: number
      high: number
      low: number
      close: number
      time: any
    }
  } = {}

  transactions.forEach((tx) => {
    const time = getTransactionsInterval(tx.timestamp, resolution)

    let tokenPriceInEth: number, ethAmount: string, tokenAmount: Big
    const event = tx.event.toLowerCase()
    switch (event) {
      case 'purchase':
        ethAmount = ethers.formatEther(tx.meta.amountIn)
        tokenAmount = amountFromWei(tx.meta.amountOut, 6)
        tokenPriceInEth = Number(ethAmount) / Number(tokenAmount)
        break
      case 'sell':
        tokenAmount = amountFromWei(tx.meta.amountIn, 6)
        ethAmount = ethers.formatEther(tx.meta.amountOut)
        tokenPriceInEth = Number(ethAmount) / Number(tokenAmount)
        break
      default:
        throw new Error('Unknown transaction type')
    }

    const price = tokenPriceInEth
    if (!groupedData[time]) {
      groupedData[time] = {
        open: price,
        high: price,
        low: price,
        close: price,
        time,
      }
    } else {
      groupedData[time].high = Math.max(
        Number(groupedData[time].high),
        Number(price),
      )
      groupedData[time].low = Math.min(
        Number(groupedData[time].low),
        Number(price),
      )
      groupedData[time].close = price
    }
  })

  return Object.values(groupedData)
}

export default useDataFeed
